<template>
  <div class="recovery-page">
    <div class="recovery-card">
      <div class="recovery-icon" aria-hidden="true">
        <i class="pi pi-envelope"></i>
      </div>
      <h1>Deze inloglink is niet meer geldig</h1>
      <p>
        Vraag direct een nieuwe link aan om uw offerte of bundel te openen.
      </p>

      <form class="recovery-form" @submit.prevent="requestMagicLink">
        <div class="form-group">
          <label for="recovery-email">E-mailadres</label>
          <InputText
            id="recovery-email"
            v-model="email"
            name="email"
            type="email"
            autocomplete="email"
            inputmode="email"
            :disabled="authStore.loading"
            required
            fluid
          />
        </div>

        <Message v-if="successMessage" severity="success" :closable="false">
          {{ successMessage }}
        </Message>
        <Message v-if="errorMessage" severity="error" :closable="false">
          {{ errorMessage }}
        </Message>
        <Message v-if="suggestOtpFallback" severity="info" :closable="false">
          Lukt de inloglink niet? Probeer het nogmaals, of log in met een eenmalige code die u per e-mail ontvangt.
        </Message>

        <Button
          type="submit"
          :loading="authStore.loading"
          label="Stuur nieuwe inloglink"
          icon="pi pi-send"
          class="w-full"
        />
        <Button
          type="button"
          label="Gebruik eenmalige code"
          :severity="suggestOtpFallback ? 'primary' : 'secondary'"
          :text="!suggestOtpFallback"
          :outlined="suggestOtpFallback"
          class="w-full"
          @click="goToOtpLogin"
        />
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import Button from 'primevue/button'
  import InputText from 'primevue/inputtext'
  import Message from 'primevue/message'
  import { useAuthStore } from '../stores/auth'
  import {
    parseRecoveryState,
    sanitizeEmailPrefill,
    sanitizeRedirectTarget,
    storePostLoginRedirect,
  } from '../utils/authNavigation'

  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  const recoveryState = parseRecoveryState(route.query)

  const email = ref(recoveryState.email)
  const successMessage = ref('')
  const errorMessage = ref('')
  const suggestOtpFallback = ref(false)

  async function requestMagicLink() {
    const normalizedEmail = sanitizeEmailPrefill(email.value)
    if (!normalizedEmail) {
      errorMessage.value = 'Vul een geldig e-mailadres in.'
      successMessage.value = ''
      return
    }

    try {
      errorMessage.value = ''
      successMessage.value = ''
      suggestOtpFallback.value = false
      storePostLoginRedirect(recoveryState.redirect)
      await authStore.signInWithMagicLink(normalizedEmail, recoveryState.redirect)
      successMessage.value = `Er is een nieuwe inloglink verstuurd naar ${normalizedEmail}.`
    } catch (error) {
      errorMessage.value = error instanceof Error
        ? error.message
        : 'Er is een fout opgetreden bij het versturen van de inloglink.'
      // Offer the one-time code flow as an alternative. Its email is delivered by
      // the same auth-send-email-hook as the magic link, so it helps when the
      // *link* is the problem (scanners consuming it, broken click-through) — not
      // when email sending itself is down.
      suggestOtpFallback.value = true
    }
  }

  function goToOtpLogin() {
    router.push({
      path: '/login',
      query: {
        redirect: sanitizeRedirectTarget(recoveryState.redirect),
        ...(sanitizeEmailPrefill(email.value) ? { email: sanitizeEmailPrefill(email.value) } : {}),
      },
    })
  }
</script>

<style scoped>
  .recovery-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background: linear-gradient(135deg, var(--p-background-0), var(--p-background-100));
  }

  .recovery-card {
    width: min(100%, 28rem);
    display: grid;
    gap: 1rem;
    padding: 2rem;
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    box-shadow: var(--shadow-lg);
  }

  .recovery-icon {
    width: 3rem;
    height: 3rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: var(--primary-alpha-10);
    color: var(--p-primary-color);
    font-size: 1.25rem;
  }

  .recovery-card h1 {
    margin: 0;
    font-size: 1.5rem;
    text-wrap: balance;
  }

  .recovery-card p {
    margin: 0;
    color: var(--p-text-muted-color);
  }

  .recovery-form {
    display: grid;
    gap: 0.875rem;
  }

  .form-group {
    display: grid;
    gap: 0.375rem;
  }

  .form-group label {
    font-weight: 600;
  }
</style>
